import { createFileRoute, Link } from "@tanstack/react-router";
import { ArticleLayout } from "@/components/ArticleLayout";
import { ogImageFor, ogMetaTags, twitterTextTags } from "@/lib/ogImages";
import { getArticleBySlug } from "@/lib/learningHub";

const SITE_URL = "https://coloradowebimpressions.com";
const PATH = "/learn/buying-an-existing-business-website";
const ARTICLE = getArticleBySlug(PATH);
const OG_IMAGE = `${SITE_URL}${ogImageFor({ articleCategory: ARTICLE?.category })}`;
const TITLE = "Buying an Existing Business? Audit the Website Before You Close";
const DESC =
  "What to inspect on a website, GBP, and backlink profile before buying a Colorado business — and how to avoid inheriting an SEO penalty or domain you don't control.";

export const Route = createFileRoute("/learn/buying-an-existing-business-website")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESC },
      { property: "og:url", content: `${SITE_URL}${PATH}` },
      { property: "og:type", content: "article" },
      ...ogMetaTags(OG_IMAGE),
      ...twitterTextTags({ title: TITLE, description: DESC }),
    ],
    links: [{ rel: "canonical", href: `${SITE_URL}${PATH}` }],
  }),
  component: Article,
});

function Article() {
  return (
    <ArticleLayout
      eyebrow="Buyer's Guide"
      read="9 min read"
      title="Buying an existing business? Audit the website before you close"
      intro="Most Colorado small-business acquisitions include the website, the GBP, and the brand — but buyers almost never audit any of them during due diligence. Here's the 30-minute audit that's saved buyers from inheriting Google penalties, expired domains, and review profiles owned by an ex-employee."
      breadcrumbs={[
        { label: "Home", to: "/" },
        { label: "Learning Hub", to: "/learn" },
        { label: "Buying an existing business? Audit the website first" },
      ]}
      path={PATH}
      faqs={[
        { q: "Why does the website matter when I'm buying the business?", a: "For most service businesses, 30–60% of new leads come from the website and Google Business Profile. If either is broken, penalized, or controlled by someone outside the deal, you've bought a business that no longer generates leads — and the seller's revenue numbers won't hold." },
        { q: "Can I inherit a Google penalty?", a: "Yes. Manual penalties follow the domain. If the previous owner bought sketchy backlinks or stuffed keywords, the site can be ranking artificially low and you won't know until your traffic drops further. Always check Google Search Console history before closing." },
        { q: "Who owns the Google Business Profile?", a: "Whoever was added as the primary owner — often the seller's personal Gmail, a former employee, or a long-gone marketing agency. Get owner transfer in writing as a closing condition, not a handshake." },
        { q: "What about the domain name?", a: "The domain is a separate asset from the website. Confirm the registrar account, login, and renewal status in writing. Domains that expire mid-transition can be auctioned within days." },
      ]}
    >
      <h2>The 30-minute pre-LOI audit</h2>
      <p>Run this <em>before</em> you sign a letter of intent. It catches the dealbreakers cheap.</p>

      <h3>1. Domain ownership and renewal</h3>
      <ul>
        <li>Run a <a href="https://lookup.icann.org/" target="_blank" rel="noopener noreferrer">WHOIS lookup</a> — is the owner the seller, a defunct agency, or an ex-spouse?</li>
        <li>Check expiration date. A domain expiring in 60 days is leverage in negotiation.</li>
        <li>Confirm registrar account credentials will transfer at closing.</li>
      </ul>

      <h3>2. Google Business Profile</h3>
      <ul>
        <li>Search the business on Google Maps. Does the listing have the right phone, hours, photos, and review count the seller claims?</li>
        <li>Ask the seller to send a screenshot of the GBP dashboard showing owner email — confirm it transfers.</li>
        <li>Count reviews in the last 90 days. A listing with no recent reviews is a listing that's stopped driving calls. <Link to="/learn/get-more-reviews">Why review velocity matters.</Link></li>
      </ul>

      <h3>3. Website traffic and rankings (the real ones)</h3>
      <ul>
        <li>Demand <strong>Google Analytics 4</strong> and <strong>Google Search Console</strong> access for 24+ months. Verify the seller's traffic claims yourself.</li>
        <li>Look for the classic seller red flag: traffic that fell off a cliff 3–12 months ago. That's usually a Google update they never recovered from.</li>
        <li>Use <a href="https://search.google.com/search-console" target="_blank" rel="noopener noreferrer">Search Console</a> to check for <em>manual actions</em> — penalties that follow the domain.</li>
      </ul>

      <h3>4. Backlink profile</h3>
      <ul>
        <li>Run the domain through Ahrefs, Semrush, or Moz (free trials work).</li>
        <li>Look for: sudden spikes in backlinks (paid link networks), foreign-language spam links, anchor text stuffed with keywords like "best plumber Denver."</li>
        <li>A clean backlink profile is an asset; a toxic one is a liability that needs disavow work. <Link to="/learn/sitemap-zero-urls-error">Related technical debt.</Link></li>
      </ul>

      <h3>5. The website itself</h3>
      <ul>
        <li>Run <a href="https://pagespeed.web.dev" target="_blank" rel="noopener noreferrer">PageSpeed Insights</a> on mobile. Score under 50 = significant rebuild needed.</li>
        <li>Check the CMS. WordPress on outdated hosting is fixable. Custom code with no documentation from a developer who's vanished is not. <Link to="/learn/custom-web-design-vs-diy-platforms">Platform context.</Link></li>
        <li>Look for SSL, mobile usability, and broken contact forms — test the form from your phone before closing.</li>
      </ul>

      <h3>6. Reviews and reputation</h3>
      <ul>
        <li>Google, Yelp, Facebook, BBB, industry-specific sites — search the business name on each.</li>
        <li>Pattern of recent 1-star reviews about service quality = the seller is selling because the wheels are coming off.</li>
        <li>Negative news search. A 5-minute Google News search has saved buyers from lawsuits-in-progress.</li>
      </ul>

      <h2>Closing-day digital handover checklist</h2>
      <p>Get all of this in the closing documents, not over text:</p>
      <ul>
        <li>Domain registrar account transfer</li>
        <li>Hosting account credentials and billing</li>
        <li>CMS / website admin logins</li>
        <li>Google Business Profile primary owner transfer (initiated before closing — Google has a 7-day delay)</li>
        <li>Google Analytics + Search Console property access</li>
        <li>Google Ads account access (if applicable)</li>
        <li>Email hosting credentials</li>
        <li>Social media account credentials</li>
        <li>Review platform logins (Yelp, BBB, industry directories)</li>
        <li>CRM, booking, and call-tracking account access</li>
      </ul>

      <h2>First 30 days after closing</h2>
      <ul>
        <li>Change every password and revoke old user accounts (especially the previous owner's email).</li>
        <li>Update GBP photos, hours, and post a "new ownership" announcement.</li>
        <li>Submit the existing sitemap to Google Search Console under your account.</li>
        <li>Do NOT redesign the website in month 1 — wait 90 days. Major changes to a stable ranking site can tank rankings overnight.</li>
        <li>Audit and disavow toxic backlinks if Ahrefs flagged any.</li>
      </ul>

      <h2>Related guides</h2>
      <ul>
        <li><Link to="/learn/first-time-website-owner-guide">First-time website owner's guide</Link></li>
        <li><Link to="/learn/new-business-website-checklist">New business website checklist</Link></li>
        <li><Link to="/learn/franchise-location-website-guide">Franchise location website guide</Link></li>
        <li><Link to="/learn/start-a-business-in-colorado">Starting a business in Colorado</Link></li>
        <li><Link to="/learn/local-seo-guide">Local SEO guide</Link></li>
        <li><Link to="/learn/google-business-profile">Google Business Profile setup</Link></li>
        <li><Link to="/learn/get-more-reviews">Getting more reviews</Link></li>
      </ul>
      <h2>Work with us</h2>
      <ul>
        <li><Link to="/web-design">Web design</Link> — custom builds for Colorado businesses</li>
        <li><Link to="/seo">SEO</Link> and <Link to="/local-seo">local SEO</Link> — get found on Google</li>
        <li><Link to="/google-maps">Google Maps optimization</Link></li>
        <li><Link to="/pricing">Transparent pricing</Link></li>
      </ul>

    </ArticleLayout>
  );
}
